import React, { useMemo, useState } from 'react';
import { IRegistroCircularizacao } from '../interfaces/ICircularizacao';

interface Props {
  registros: IRegistroCircularizacao[];
  onAbrirPlanejamento: (registro: IRegistroCircularizacao) => void;
}

const formatarData = (iso?: string) => {
  if (!iso) return '—';
  const [ano, mes, dia] = iso.split('-');
  return dia ? `${dia}/${mes}/${ano}` : iso;
};

const diasEntre = (inicio?: string, fim?: string): number | undefined => {
  if (!inicio || !fim) return undefined;
  const ms = new Date(fim).getTime() - new Date(inicio).getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
};

const BADGE_PREPARACAO: Record<string, { label: string; cls: string }> = {
  NAO_INICIADA: { label: 'Não iniciada', cls: 'badge-light-secondary' },
  EM_ANDAMENTO: { label: 'Em andamento', cls: 'badge-light-warning' },
  PRONTA: { label: 'Carta pronta', cls: 'badge-light-success' },
  NAO_SE_APLICA: { label: 'Não se aplica', cls: 'badge-light-secondary' },
};

// Seção 7 — "Planejamento das Circularizações": tela dedicada com todos os destinatários ativos,
// data de envio, prazo, contato, situação e dias entre envio/prazo. Usabilidade da seção 7.1:
// ordenar por data de resposta, filtrar destinatários sem prazo, identificar datas vencidas.
const PlanejamentoCircularizacaoTable: React.FC<Props> = ({ registros, onAbrirPlanejamento }) => {
  const [ordenacao, setOrdenacao] = useState<'asc' | 'desc' | null>(null);
  const [somenteSemPrazo, setSomenteSemPrazo] = useState(false);
  const hojeIso = new Date().toISOString().slice(0, 10);

  const linhas = useMemo(() => {
    let lista = [...registros];
    if (somenteSemPrazo) {
      lista = lista.filter((r) => !r.dataPrevistaEnvio || !r.prazoResposta);
    }
    if (ordenacao) {
      lista.sort((a, b) => {
        const pa = a.prazoResposta ?? (ordenacao === 'asc' ? '9999-99-99' : '');
        const pb = b.prazoResposta ?? (ordenacao === 'asc' ? '9999-99-99' : '');
        return ordenacao === 'asc' ? pa.localeCompare(pb) : pb.localeCompare(pa);
      });
    }
    return lista;
  }, [registros, somenteSemPrazo, ordenacao]);

  const alternarOrdenacao = () => {
    setOrdenacao((atual) => (atual === null ? 'asc' : atual === 'asc' ? 'desc' : null));
  };

  const semPrazoCount = registros.filter((r) => !r.dataPrevistaEnvio || !r.prazoResposta).length;

  return (
    <div>
      <div className='d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2'>
        <div className='form-check'>
          <input
            id='circ-filtro-sem-prazo'
            type='checkbox'
            className='form-check-input'
            checked={somenteSemPrazo}
            onChange={(e) => setSomenteSemPrazo(e.target.checked)}
          />
          <label htmlFor='circ-filtro-sem-prazo' className='form-check-label' style={{ fontSize: 13 }}>
            Mostrar só destinatários sem prazo definido ({semPrazoCount})
          </label>
        </div>
        <div className='text-muted' style={{ fontSize: 12 }}>
          {linhas.length} destinatário{linhas.length === 1 ? '' : 's'} ativo{linhas.length === 1 ? '' : 's'}
        </div>
      </div>

      {linhas.length === 0 ? (
        <div className='circ-empty-state'>
          <i className='bi bi-calendar-event circ-empty-state-icon' />
          <div className='circ-empty-state-title'>Nenhum destinatário para planejar</div>
          <div className='circ-empty-state-texto'>
            {somenteSemPrazo ? 'Todos os destinatários ativos já têm prazo definido.' : 'Inclua empresas na aba Ativas para planejar o envio.'}
          </div>
        </div>
      ) : (
        <div className='table-responsive'>
          <table className='table table-sm table-row-dashed table-row-gray-200 align-middle gs-0 gy-2 fs-8'>
            <thead>
              <tr className='fw-bolder text-muted'>
                <th>Empresa</th>
                <th>Data Prevista de Envio</th>
                <th style={{ cursor: 'pointer' }} onClick={alternarOrdenacao} title='Ordenar por data de resposta'>
                  Prazo para Resposta{' '}
                  {ordenacao === 'asc' && <i className='bi bi-sort-up' />}
                  {ordenacao === 'desc' && <i className='bi bi-sort-down' />}
                  {!ordenacao && <i className='bi bi-arrow-down-up' style={{ fontSize: 10, opacity: 0.5 }} />}
                </th>
                <th>Contato Responsável</th>
                <th>E-mail</th>
                <th>Telefone</th>
                <th>Situação da Preparação</th>
                <th className='text-center'>Dias Envio → Prazo</th>
                <th className='text-end'>Ações</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((registro) => {
                const contatoPrincipal = registro.contatos.find((c) => c.contatoPrincipal) ?? registro.contatos[0];
                const vencido = !!registro.prazoResposta && registro.prazoResposta < hojeIso;
                const dias = diasEntre(registro.dataPrevistaEnvio, registro.prazoResposta);
                const preparacao = BADGE_PREPARACAO[registro.situacaoPreparacaoCarta];
                return (
                  <tr key={registro.id} style={vencido ? { background: '#fdecec' } : undefined}>
                    <td style={{ fontWeight: 600 }}>{registro.razaoSocialExibicao}</td>
                    <td>{formatarData(registro.dataPrevistaEnvio)}</td>
                    <td>
                      {formatarData(registro.prazoResposta)}
                      {vencido && (
                        <span className='badge badge-light-danger ms-2' title='Prazo de resposta já vencido'>
                          Vencido
                        </span>
                      )}
                      {!registro.prazoResposta && (
                        <span className='badge badge-light-warning ms-2'>Sem prazo</span>
                      )}
                    </td>
                    <td>{contatoPrincipal?.nomeCompleto ?? '—'}</td>
                    <td>{contatoPrincipal?.emailPrincipal ?? '—'}</td>
                    <td>{contatoPrincipal?.telefone ?? '—'}</td>
                    <td>
                      <span className={`badge ${preparacao.cls}`}>{preparacao.label}</span>
                    </td>
                    <td className='text-center'>{dias !== undefined ? dias : '—'}</td>
                    <td className='text-end'>
                      <button type='button' className='btn btn-sm btn-light-primary' onClick={() => onAbrirPlanejamento(registro)}>
                        <i className='bi bi-calendar-event me-1' /> Definir Prazos
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PlanejamentoCircularizacaoTable;
